import React from "react";
import {
  Popover,
  Stack,
  Typography,
  Chip,
  Box,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { FilterName, OfficerFilter } from "../types";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { useReportFilters } from "../hooks/useReportFilters";

interface FilterPopoversProps {
  filterPopover: { name: FilterName | null; anchorEl: HTMLElement | null };
  onClose: () => void;
  filters: ReturnType<typeof useReportFilters>;
  availableReporters: OfficerFilter[];
  availableModifiers: OfficerFilter[];
  allReportStatuses: ReportStatus[];
}

export const FilterPopovers: React.FC<FilterPopoversProps> = ({
  filterPopover,
  onClose,
  filters,
  availableReporters,
  availableModifiers,
  allReportStatuses,
}) => {
  const statusLabel = (s: ReportStatus) =>
    s === ReportStatus.RMS_COMMITTED
      ? "SUBMITTED"
      : s === ReportStatus.AWAITING_INPUT
        ? "AWAITING INPUT"
        : s;

  return (
    <Popover
      open={Boolean(filterPopover.name)}
      anchorEl={filterPopover.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      disableRestoreFocus
      PaperProps={{
        sx: { p: 2, width: { xs: 320, xxl: 400, uhd: 480 }, maxWidth: "95vw" },
      }}
    >
      {/* Modified Date */}
      {filterPopover.name === "modifiedDate" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Modified Date
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Today"
              onClick={() => filters.applyQuickRange("modified", "today")}
            />
            <Chip
              label="Last 7 days"
              onClick={() => filters.applyQuickRange("modified", "last7")}
            />
            <Chip
              label="Last 30 days"
              onClick={() => filters.applyQuickRange("modified", "last30")}
            />
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={filters.dateFrom}
              onChange={filters.handleDateFromChange}
              maxDate={filters.dateTo || filters.maxDateFrom}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
            <DatePicker
              label="To"
              value={filters.dateTo}
              onChange={filters.handleDateToChange}
              maxDate={dayjs()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </LocalizationProvider>
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => filters.applyQuickRange("modified", "clear")}
            >
              Clear
            </Button>
            <Button onClick={onClose} size="small">
              Done
            </Button>
          </Box>
        </Stack>
      )}

      {/* Reported Date */}
      {filterPopover.name === "reportedDate" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Reported Date
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Today"
              onClick={() => filters.applyQuickRange("reported", "today")}
            />
            <Chip
              label="Last 7 days"
              onClick={() => filters.applyQuickRange("reported", "last7")}
            />
            <Chip
              label="Last 30 days"
              onClick={() => filters.applyQuickRange("reported", "last30")}
            />
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={filters.reportedDateFrom}
              onChange={filters.handleReportedDateFromChange}
              maxDate={filters.reportedDateTo || dayjs()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
            <DatePicker
              label="To"
              value={filters.reportedDateTo}
              onChange={filters.handleReportedDateToChange}
              maxDate={dayjs()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </LocalizationProvider>
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => filters.applyQuickRange("reported", "clear")}
            >
              Clear
            </Button>
            <Button onClick={onClose} size="small">
              Done
            </Button>
          </Box>
        </Stack>
      )}

      {/* Reported By */}
      {filterPopover.name === "reportedBy" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Reported By
          </Typography>
          {availableReporters.length > 0 ? (
            <Autocomplete
              multiple
              open
              options={availableReporters}
              value={filters.reportedByFilter}
              onChange={(_, newValue) => filters.setReportedByFilter(newValue)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              disablePortal
              limitTags={3}
              forcePopupIcon={false}
              ListboxProps={{ style: { maxHeight: 250 } }}
              slotProps={{
                popper: {
                  sx: {
                    position: "relative !important",
                    transform: "none !important",
                  },
                  disablePortal: true,
                },
                paper: {
                  sx: { boxShadow: "none", mt: 1 },
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                    {option.name}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search names..."
                  size="small"
                  autoFocus
                />
              )}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reporters found
            </Typography>
          )}
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => {
                filters.setReportedByFilter([]);
              }}
            >
              Clear
            </Button>
            <Button size="small" onClick={onClose}>
              Done
            </Button>
          </Box>
        </Stack>
      )}

      {/* Modified By */}
      {filterPopover.name === "modifiedBy" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Modified By
          </Typography>
          {availableModifiers.length > 0 ? (
            <Autocomplete
              multiple
              open
              options={availableModifiers}
              value={filters.modifiedByFilter}
              onChange={(_, newValue) => filters.setModifiedByFilter(newValue)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              disablePortal
              limitTags={3}
              forcePopupIcon={false}
              ListboxProps={{ style: { maxHeight: 250 } }}
              slotProps={{
                popper: {
                  sx: {
                    position: "relative !important",
                    transform: "none !important",
                  },
                  disablePortal: true,
                },
                paper: {
                  sx: { boxShadow: "none", mt: 1 },
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                    {option.name}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search names..."
                  size="small"
                  autoFocus
                />
              )}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No modifiers found
            </Typography>
          )}
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => {
                filters.setModifiedByFilter([]);
              }}
            >
              Clear
            </Button>
            <Button size="small" onClick={onClose}>
              Done
            </Button>
          </Box>
        </Stack>
      )}

      {/* Status */}
      {filterPopover.name === "status" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Status
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Select All"
              onClick={() => {
                filters.setSelectedStatuses(allReportStatuses);
              }}
            />
          </Stack>
          <FormGroup>
            {allReportStatuses.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={filters.selectedStatuses.includes(status)}
                    onChange={(e) => {
                      filters.setSelectedStatuses((prev) =>
                        e.target.checked
                          ? [...prev, status]
                          : prev.filter((s) => s !== status)
                      );
                    }}
                    size="small"
                  />
                }
                label={
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "bold",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      ...(status === "PENDING REVIEW" && {
                        backgroundColor: "rgba(230, 81, 0, 0.15)",
                        color: "warning.main",
                      }),
                      ...(status === "AWAITING INPUT" && {
                        backgroundColor: "rgba(245, 124, 0, 0.15)",
                        color: "warning.main",
                      }),
                      ...(status === "SIGNED" && {
                        backgroundColor: "rgba(13, 71, 161, 0.15)",
                        color: "info.main",
                      }),
                      ...(status === "RMS COMMITTED" && {
                        backgroundColor: "rgba(27, 94, 32, 0.15)",
                        color: "success.main",
                      }),
                    }}
                  >
                    {statusLabel(status)}
                  </Box>
                }
                sx={{ m: 0 }}
              />
            ))}
          </FormGroup>
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => {
                filters.setSelectedStatuses([]);
              }}
            >
              Clear
            </Button>
            <Button size="small" onClick={onClose}>
              Done
            </Button>
          </Box>
        </Stack>
      )}
    </Popover>
  );
};
